/**
 * Loads one issue's coalition map, plus the stances behind it so a node panel
 * can show *why* someone holds the position the map draws.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { IssuesUnavailableError, getIssueMap, issuesErrorMessage, listStances } from '../issuesApi';
import type { IssueMap, StanceView } from '../../../types/issues';
import { MapModel, buildMapModel, subjectKey } from './mapModel';

export interface IssueMapState {
  map: IssueMap | null;
  model: MapModel;
  /** Stance by `type:id`, for rationale and provenance; empty if unreadable */
  stances: Map<string, StanceView>;
  loading: boolean;
  error: string | null;
  /** The server predates this feature — a different message from a failure */
  unavailable: boolean;
  reload: () => void;
}

/** A 404 here means the endpoint is not deployed, not "no map for this issue" */
function isMissing(error: any): boolean {
  return error instanceof IssuesUnavailableError || error?.response?.status === 404;
}

export function useIssueMap(issueId: string): IssueMapState {
  const [map, setMap] = useState<IssueMap | null>(null);
  const [stances, setStances] = useState<Map<string, StanceView>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!issueId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const next = await getIssueMap(issueId);
        if (cancelled) return;
        setMap(next);
        setError(null);
        setUnavailable(false);
      } catch (err) {
        if (cancelled) return;
        setMap(null);
        setUnavailable(isMissing(err));
        setError(isMissing(err) ? 'This server does not have the coalition map yet' : issuesErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Rationale is a bonus: a failure here must not break the map
      try {
        const list = await listStances(issueId);
        if (cancelled) return;
        setStances(new Map(list.map((stance) => [subjectKey(stance.subject), stance])));
      } catch {
        if (!cancelled) setStances(new Map());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [issueId, nonce]);

  const model = useMemo(() => buildMapModel(map), [map]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { map, model, stances, loading, error, unavailable, reload };
}
